import type { WorkingTreeChangeRepoDetail, WorkingTreeRepoChangesBasis } from '../../git/changes/index.js';
import {
  commitSessionWorktree,
  getSessionWorkingTreeChangeDetails,
  getSessionWorkingTreeChangeFilePatch,
  getSessionWorkingTreeStatus,
  pushSessionUpstream,
  removeSessionWorktreeCheckouts,
  renameSessionWorktreeBranch,
} from './git/index.js';
import type { SessionWorkingTreeChangeFilePatchOpts } from './session-worktree-manager-types.js';
import type { SessionWorktreeCache } from './session-worktree-cache.js';
import type { SessionWorktreePathsContext } from './session-worktree-paths.js';
import { createSessionWorktreePathsApi } from './session-worktree-paths.js';

export function createSessionWorktreeGitApi(
  ctx: SessionWorktreePathsContext & { cache: SessionWorktreeCache; log: (msg: string) => void },
) {
  const paths = createSessionWorktreePathsApi(ctx);

  return {
    async renameSessionBranch(sessionId: string, newBranch: string): Promise<void> {
      await renameSessionWorktreeBranch(ctx.cache, sessionId, newBranch, ctx.log);
    },
    async removeSessionWorktrees(sessionId: string): Promise<void> {
      await removeSessionWorktreeCheckouts(ctx.cache, sessionId, ctx.worktreesRootPath, ctx.log);
    },
    async commitSession(params: {
      sessionId: string;
      branch: string;
      message: string;
      push: boolean;
    }): Promise<{ ok: boolean; error?: string }> {
      return commitSessionWorktree(ctx.cache, params);
    },
    async getSessionWorkingTreeStatus(sessionId: string): Promise<{
      hasUncommittedChanges: boolean;
      hasUnpushedCommits: boolean;
      uncommittedFileCount: number;
    }> {
      return getSessionWorkingTreeStatus(ctx.cache, sessionId, (sid) => paths.discoverAsync(sid));
    },
    async getSessionWorkingTreeChangeDetails(
      sessionId: string,
      opts?: { repoRelPath?: string; basis?: WorkingTreeRepoChangesBasis; recentCommitsLimit?: unknown },
    ): Promise<WorkingTreeChangeRepoDetail[]> {
      return getSessionWorkingTreeChangeDetails(ctx.cache, sessionId, (sid) => paths.discoverAsync(sid), opts);
    },
    async getSessionWorkingTreeChangeFilePatch(
      sessionId: string,
      opts: SessionWorkingTreeChangeFilePatchOpts,
    ): Promise<{ patchContent?: string; totalLines: number | null }> {
      return getSessionWorkingTreeChangeFilePatch(ctx.cache, sessionId, (sid) => paths.discoverAsync(sid), opts);
    },
    async pushSessionUpstream(sessionId: string): Promise<{ ok: boolean; error?: string }> {
      return pushSessionUpstream(ctx.cache, sessionId, (sid) => paths.discoverAsync(sid));
    },
  };
}
